import {
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Switch,
  TextField,
  Tooltip,
} from '@material-ui/core';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { cloneDeep } from 'lodash-es';
import React, { useEffect, useState } from 'react';

import { ABTestMode } from '~/src/dash/api/ab-test';
import { EBool } from '~/src/dash/api/types/bool';
import {
  DateTimeRangeComponent,
  IDateRangeChangeEvent,
} from '~/src/dash/components/datepicker/date-time-range-component';
import { SelectPill } from '~/src/dash/components/dropdowns/SelectPill';
import { ModernTextField } from '~/src/dash/components/modern-text-field';
import { genTimezoneList } from '~/src/dash/globals/timezone';
import {
  ButtonV2,
  Col,
  Cols,
  Div,
  fontSizing,
  Row,
  Rows,
  Text,
  TextButton,
} from '~/src/dash/publicaUI';
import { IListItem } from '~/src/dash/types/menuList';
import { publicaColors } from '~/src/dash/utils/colors';
import {
  formatDateFromUTCToOffset,
  formatDateUsingTimezone,
  formatDateWithOffsetToUTC,
} from '~/src/dash/utils/date';
import { smallRadius } from '~/src/dash/utils/material-dashboard-react';
import { useBaseStore } from '~/src/dash/views/base/contexts';
import { IChannelABTest } from '~/src/dash/views/channels/api';

interface IChannelABTestProps {
  onChange?: (test: IChannelABTest) => void;
  onCreateTest?: (test: IChannelABTest) => void;
  onCancelTest?: () => void;
  onEndTest?: (test: IChannelABTest) => void;
  onImplementTestParams?: (test: IChannelABTest) => void;
  test: IChannelABTest;
  testType: ABTestMode;
}

interface IChannelABTestErrors {
  Name: string | null;
  TrafficShareTestB: string | null;
  UseSyntheticIDTestB: string | null;
}

export const ChannelABTest = ({
  onChange,
  onCreateTest,
  onCancelTest,
  onEndTest,
  // onImplementTestParams, // V2
  test,
  testType,
}: IChannelABTestProps) => {
  const defaultErrors = {
    Name: null,
    TrafficShareTestB: null,
    UseSyntheticIDTestB: null,
  } as IChannelABTestErrors;
  const { session } = useBaseStore();
  const [isCollapsed, setIsCollapsed] = useState(testType !== ABTestMode.Past ? false : true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [timezones, setTimeZones] = useState(genTimezoneList(test.Timezone));
  const [errors, setErrors] = useState<IChannelABTestErrors>(defaultErrors);

  // helper to update test params to the appropriate format and value
  const onChangeTestParams = (keys: Array<keyof IChannelABTest>, values: Array<any>) => {
    if (!onChange) return null;
    const updatedTest = cloneDeep(test);
    keys.forEach((key, idx) => {
      const value = values[idx];

      switch (key) {
        case 'TrafficShareTestB': {
          const trafficShareTestB = value === '' ? 0 : parseInt(value, 10);
          if (isNaN(trafficShareTestB)) return null;
          let trafficShareTestA = 100 - trafficShareTestB;
          if (trafficShareTestA > 100) trafficShareTestA = 100;
          if (trafficShareTestA < 0) trafficShareTestA = 0;
          updatedTest.TrafficShareTestB = trafficShareTestB;
          updatedTest.TrafficShareTestA = trafficShareTestA;
          break;
        }

        case 'UseSyntheticIDTestB': {
          updatedTest.UseSyntheticIDTestB = value;
          break;
        }

        case 'UseTruncatedIPTestB': {
          updatedTest.UseTruncatedIPTestB = value;
          break;
        }

        case 'Name': {
          updatedTest.Name = value;
          break;
        }

        case 'StartTime': {
          updatedTest.StartTime = formatDateWithOffsetToUTC(value, updatedTest.Timezone);
          break;
        }

        case 'EndTime': {
          updatedTest.EndTime = formatDateWithOffsetToUTC(value, updatedTest.Timezone);
          break;
        }

        case 'Timezone': {
          updatedTest.Timezone = value;
          break;
        }

        default: {
          break;
        }
      }
      return;
    });
    onChange(updatedTest);
    return;
  };

  // helper to validate test params
  const validateTest = (newTest: IChannelABTest) => {
    const newErrors = { ...errors };
    for (const key of Object.keys(defaultErrors)) {
      switch (key) {
        case 'TrafficShareTestB': {
          if (newTest.TrafficShareTestB >= 100 || newTest.TrafficShareTestB <= 0) {
            newErrors.TrafficShareTestB =
              'Share of requests allocated to test must be greater than 0% and less than 100%';
          } else {
            newErrors.TrafficShareTestB = null;
          }
          break;
        }

        case 'Name': {
          if (!newTest.Name) {
            newErrors.Name = 'Test needs a valid name';
          } else {
            newErrors.Name = null;
          }
        }

        default:
          break;
      }
    }
    setErrors(newErrors);
  };

  // validate test params whenever the test is updated to display appropriate error messages
  useEffect(() => {
    if (testType === ABTestMode.New) validateTest(test);
  }, [test]);

  return (
    <Div
      style={{
        background:
          testType === ABTestMode.Past ? publicaColors.veryLightGray : publicaColors.white,
        boxShadow: `0px 3px 6px ${publicaColors.mediumGray}`,
        borderRadius: smallRadius,
      }}
    >
      <Cols
        basePadding
        largeHorizontalPadding
        style={{
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: `1px solid ${publicaColors.lightGray}`,
          cursor: 'pointer',
        }}
        onClick={() => setIsCollapsed(!isCollapsed)}
      >
        <Col>
          {testType === ABTestMode.New ? (
            <ModernTextField
              value={test.Name}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                e.stopPropagation();
                onChangeTestParams(['Name'], [e.target.value]);
              }}
              customStyle={{ paddingLeft: 0 }}
            />
          ) : (
            <Text bold muted={testType === ABTestMode.Past}>
              {test.Name}
            </Text>
          )}
          {errors.Name && (
            <Text extraSmall color={publicaColors.red}>
              {errors.Name}
            </Text>
          )}
        </Col>
        <Col>
          {isCollapsed ? (
            <ExpandLessIcon
              style={{
                color: testType === ABTestMode.Past ? publicaColors.gray : 'inherit',
              }}
            />
          ) : (
            <ExpandMoreIcon
              style={{
                color: testType === ABTestMode.Past ? publicaColors.gray : 'inherit',
              }}
            />
          )}
        </Col>
      </Cols>

      <Collapse style={{ width: '100%' }} in={!isCollapsed}>
        <Rows
          largePadding
          style={{
            borderBottom: `1px solid ${publicaColors.lightGray}`,
          }}
        >
          <Row noPadding>
            <Text title muted={testType === ABTestMode.Past}>
              Flight Dates
            </Text>
          </Row>
          <Row noPadding>
            {testType === ABTestMode.New ? (
              <Cols
                wrap
                style={{
                  alignItems: 'end',
                }}
              >
                <Col marginRight='2.5em'>
                  <DateTimeRangeComponent
                    timezone={session.publisher.Timezone}
                    start={formatDateFromUTCToOffset(test.StartTime, test.Timezone)}
                    end={formatDateFromUTCToOffset(test.EndTime, test.Timezone)}
                    onChange={({ start, end }: Partial<IDateRangeChangeEvent>) => {
                      onChangeTestParams(['StartTime', 'EndTime'], [start, end]);
                    }}
                    enablePresets
                  />
                </Col>
                <Col style={{ padding: '9px 0' }}>
                  <Text small muted style={{ marginBottom: '0.15em' }}>
                    Timezone
                  </Text>
                  <SelectPill
                    customLabel={test.Timezone}
                    currentSelected={{ [test.Timezone]: timezones[test.Timezone] }}
                    options={timezones}
                    selectedLimit={1}
                    handleAccept={(
                      _key: string,
                      allOptions: Record<string, IListItem>,
                      selectedOptions: Record<string, IListItem>,
                    ) => {
                      setTimeZones(allOptions);
                      onChangeTestParams(['Timezone'], [Object.keys(selectedOptions)[0]]);
                    }}
                    displayArrow
                    isActive={false}
                    filterKey='Timezones'
                  />
                </Col>
              </Cols>
            ) : (
              <Cols
                style={{
                  alignItems: 'center',
                }}
              >
                <Col paddingRight='0.5em'>
                  <Text muted={testType === ABTestMode.Past}>
                    {formatDateUsingTimezone(test.StartTime, test.Timezone)}
                  </Text>
                </Col>
                <Col paddingRight='0.5em'>
                  <Text muted={testType === ABTestMode.Past}>—</Text>
                </Col>
                <Col paddingRight='2em'>
                  <Text muted={testType === ABTestMode.Past}>
                    {formatDateUsingTimezone(test.EndTime, test.Timezone)}
                  </Text>
                </Col>
                <Col>
                  <Text muted={testType === ABTestMode.Past}>{test.Timezone}</Text>
                </Col>
              </Cols>
            )}
          </Row>
        </Rows>

        <Cols
          wrap
          noPadding
          style={{
            alignItems: 'center',
            borderBottom: `1px solid ${publicaColors.lightGray}`,
          }}
        >
          <Col flex largePadding style={{ borderRight: `1px solid ${publicaColors.lightGray}` }}>
            <Text title style={{ paddingBottom: '0.5em' }} muted={testType === ABTestMode.Past}>
              A - Current Parameters
            </Text>
            <Text small muted={testType === ABTestMode.Past} style={{ paddingBottom: '0.5em' }}>
              Percentage of Traffic: {test.TrafficShareTestA + ' %'}
            </Text>
            <Cols
              style={{
                alignItems: 'center',
              }}
            >
              <Col paddingRight='0.5em'>
                <Text small muted={testType === ABTestMode.Past}>
                  Use Synthetic ID:
                </Text>
              </Col>
              <Col>
                <Switch
                  checked={EBool.isTrue(test.UseSyntheticIDTestA)}
                  color='secondary'
                  style={{ cursor: 'default', pointerEvents: 'none' }}
                />
              </Col>
            </Cols>
            <Cols
              style={{
                alignItems: 'center',
              }}
            >
              <Col paddingRight='0.5em'>
                <Text small muted={testType === ABTestMode.Past}>
                  Use Truncated IP:
                </Text>
              </Col>
              <Col>
                <Switch
                  checked={EBool.isTrue(test.UseTruncatedIPTestA)}
                  color='secondary'
                  style={{ cursor: 'default', pointerEvents: 'none' }}
                />
              </Col>
            </Cols>
          </Col>

          <Col flex largePadding>
            <Text title muted={testType === ABTestMode.Past} style={{ paddingBottom: '0.5em' }}>
              B - Test Parameters
            </Text>
            {testType === ABTestMode.New ? (
              <>
                <Cols
                  style={{
                    alignItems: 'center',
                  }}
                >
                  <Col paddingRight='1em'>
                    <Text small>Percentage of Traffic:</Text>
                  </Col>
                  <Col paddingRight='0.5em'>
                    <TextField
                      name='TrafficShareTestB'
                      value={test.TrafficShareTestB}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        onChangeTestParams(['TrafficShareTestB'], [e.target.value])
                      }
                      error={!!errors.TrafficShareTestB}
                      style={{ width: '3em' }}
                      inputProps={{
                        style: { textAlign: 'right', fontSize: fontSizing.small },
                      }}
                    />
                  </Col>
                  <Col>
                    <Text small>%</Text>
                  </Col>
                </Cols>
                {errors.TrafficShareTestB && (
                  <Text extraSmall color={publicaColors.red}>
                    {errors.TrafficShareTestB}
                  </Text>
                )}
              </>
            ) : (
              <Text small muted={testType === ABTestMode.Past} style={{ paddingBottom: '0.5em' }}>
                Percentage of Traffic: {test.TrafficShareTestB + ' %'}
              </Text>
            )}
            <Cols
              style={{
                alignItems: 'center',
              }}
            >
              <Col paddingRight='0.5em'>
                <Text small muted={testType === ABTestMode.Past}>
                  Use Synthetic ID:
                </Text>
              </Col>
              <Col>
                <Switch
                  checked={EBool.isTrue(test.UseSyntheticIDTestB)}
                  onChange={() =>
                    testType === ABTestMode.New &&
                    onChangeTestParams(
                      ['UseSyntheticIDTestB'],
                      [
                        EBool.isTrue(test.UseSyntheticIDTestB)
                          ? EBool.EBool_FALSE
                          : EBool.EBool_TRUE,
                      ],
                    )
                  }
                  style={
                    testType === ABTestMode.New ? {} : { cursor: 'default', pointerEvents: 'none' }
                  }
                  color='secondary'
                />
              </Col>
            </Cols>
            <Cols
              style={{
                alignItems: 'center',
              }}
            >
              <Col paddingRight='0.5em'>
                <Text small muted={testType === ABTestMode.Past}>
                  Use Truncated IP:
                </Text>
              </Col>
              <Col>
                <Tooltip title='Only apply to IPv4 and not IPv6'>
                  <Switch
                    checked={EBool.isTrue(test.UseTruncatedIPTestB)}
                    onChange={() =>
                      testType === ABTestMode.New &&
                      onChangeTestParams(
                        ['UseTruncatedIPTestB'],
                        [
                          EBool.isTrue(test.UseTruncatedIPTestB)
                            ? EBool.EBool_FALSE
                            : EBool.EBool_TRUE,
                        ],
                      )
                    }
                    style={
                      testType === ABTestMode.New
                        ? {}
                        : { cursor: 'default', pointerEvents: 'none' }
                    }
                    color='secondary'
                  />
                </Tooltip>
              </Col>
            </Cols>
          </Col>
        </Cols>

        {testType !== ABTestMode.Past && (
          <Cols
            basePadding
            largeHorizontalPadding
            style={{
              alignItems: 'center',
            }}
          >
            {testType === ABTestMode.New && (
              <>
                {onCreateTest && (
                  <Col paddingRight='1em'>
                    <ButtonV2 onClick={() => setIsDialogOpen(true)}>Launch Test</ButtonV2>
                  </Col>
                )}
                {onCancelTest && (
                  <Col>
                    <TextButton onClick={onCancelTest}>Cancel</TextButton>
                  </Col>
                )}
              </>
            )}
            {testType === ABTestMode.Current && onEndTest && (
              <Col>
                <ButtonV2 onClick={() => onEndTest(test)}>End Test</ButtonV2>
              </Col>
            )}
            {/* V2 */}
            {/* {testType === ABTestMode.Past && onImplementTestParams && (
              <Col>
                <ButtonV2 onClick={onImplementTestParams}>Implement Test Parameters</ButtonV2>
              </Col>
            )} */}
          </Cols>
        )}
      </Collapse>

      <Dialog open={isDialogOpen} onClose={() => setIsDialogOpen(false)}>
        <DialogTitle>Confirm Launch Test</DialogTitle>
        <DialogContent>
          <Text style={{ paddingBottom: '1em' }}>
            Test parameters will be set and requests will start splitting between Channel Parameters
            A and Channel Test Parameters B.
          </Text>
          <Text color={publicaColors.red}>
            Launching will prevent changes on the source channel’s test parameters.
          </Text>
        </DialogContent>
        <DialogActions>
          <Cols
            basePadding
            style={{
              alignItems: 'center',
            }}
          >
            <Col paddingRight='1em'>
              <ButtonV2 onClick={() => onCreateTest && onCreateTest(test)}>Confirm</ButtonV2>
            </Col>
            <Col>
              <TextButton onClick={() => setIsDialogOpen(false)}>Cancel</TextButton>
            </Col>
          </Cols>
        </DialogActions>
      </Dialog>
    </Div>
  );
};
